import React from 'react';
import EngagementStageBadge from '../workflow/EngagementStageBadge';
import { formatDateShort } from '../../utils/dateFormat';
import NowName, { StaffNames, nowLabel } from './NowName';
import { shortEngagementName } from '../workflow/CoverageDisplay';
import { serviceLabel } from '../../utils/engagementName';

/**
 * "Agreed Upon Procedures #2" plus, for services that allow several
 * engagements per company and FYE, the description headline underneath
 * (the API's serviceDescription — see utils/engagementName engagementTag).
 */
export const ServiceCell = ({ r, className = 'text-slate-600' }) => (
  <span className={className}>
    <span>{serviceLabel(r.serviceProvided, r.engagementSeq) || '—'}</span>
    {r.serviceDescription && (
      <span className="block max-w-[16rem] truncate text-xs text-slate-400" title={r.serviceDescription}>
        {r.serviceDescription}
      </span>
    )}
  </span>
);

// Who leads the engagement, in one cell. An engagement holds ONE partner but
// may hold several directors and several managers, so each role is read off
// its array (partners / directors / managers = [{staffId, staffName,
// staffNameNow}]); the legacy single column is the fallback for rows written
// before the leads table existed. Same rule as the Excel download.
//
// One line per role, the role marked by a muted letter. A role with nobody
// prints no line at all, so most rows stay one or two lines high.
const LEAD_ROLES = {
  partner: { mark: 'P', list: 'partners', single: 'partner' },
  director: { mark: 'D', list: 'directors', single: 'director' },
  manager: { mark: 'M', list: 'managers', single: 'manager' },
};

// One row of the cell. The names stay on ONE line, clipped with an ellipsis
// and the whole list in the tooltip: an engagement may carry three directors
// and three managers, and letting those wrap turns one table row into six
// lines — and wraps mid-name ("Andrew Chin Weng / Keong"), which reads as two
// different people. The full list is on the detail page.
const LeadLine = ({ mark, people, single, singleNow }) => {
  const title = people.length > 0
    ? people.map((m) => nowLabel(m.staffName, m.staffNameNow)).join(', ')
    : nowLabel(single || '', singleNow);
  return (
    <span className="flex items-baseline gap-1.5">
      {mark && <span className="w-3 shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-500">{mark}</span>}
      <span className="min-w-0 flex-1 truncate text-slate-600" title={title}>
        {people.length > 0
          ? <StaffNames list={people} />
          : <NowName value={single} now={singleNow} />}
      </span>
    </span>
  );
};

// showMarks=false drops the role letters — for a column headed with the one
// role it prints (Partner), where repeating "P" on every row says nothing.
export const LeadsCell = ({ r, roles = ['director', 'manager'], showMarks = true }) => {
  const lines = roles.map((key) => {
    const role = LEAD_ROLES[key];
    const people = (r[role.list] || []).filter((m) => m && m.staffName);
    const single = r[role.single];
    const singleNow = r[`${role.single}Now`];
    if (people.length === 0 && !single && !singleNow) return null;
    return <LeadLine key={key} mark={showMarks ? role.mark : null} people={people} single={single} singleNow={singleNow} />;
  }).filter(Boolean);
  if (lines.length === 0) return <span className="text-slate-400">—</span>;
  return <span className="flex flex-col gap-0.5">{lines}</span>;
};

/**
 * A date column cell: '30 Sep' and '2026' each unbreakable, so a narrow column
 * breaks once before the year and never stacks day / month / year on three
 * lines. Wide enough and it stays on one line.
 */
export const DateCell = ({ value }) => {
  const s = value ? formatDateShort(value) : '';
  if (!s) return <span className="text-slate-400">—</span>;
  const cut = s.lastIndexOf(' ');
  if (cut < 0) return <span className="whitespace-nowrap">{s}</span>;
  return (
    <>
      <span className="whitespace-nowrap">{s.slice(0, cut)}</span>{' '}
      <span className="whitespace-nowrap">{s.slice(cut + 1)}</span>
    </>
  );
};

// The ONE engagement list column set. Two surfaces render
// the same table — Management → Engagements (/qm/engagements, WorkflowListShell)
// and My Records → Engagements (/my?tab=engagements, embedded shell). They must
// describe an engagement identically, so the columns live here; the surfaces
// differ only in scope (all vs mine), row target and toolbar.
//
// Both endpoints go through engagementsRepository.list, so every field —
// including the derived currentStage — is present on either side.
const fmtDate = (v) => (v ? formatDateShort(v) : '—');

// No File Ref. column: the reference stays on the detail page and is still
// matched by the search box.
// showAudit=false drops Sign Off and Archival Deadline (My Records, viewer
// without an Audit key — canSeeAuditStages).
export const engagementListColumns = ({ showAudit = true } = {}) => [
  // Names are the engagement's own snapshots; a renamed client master shows
  // "(now: …)" after them (docs/data-model.md §2).
  {
    key: 'clientGroup',
    label: 'Client Group',
    sortKey: 'clientGroup',
    className: 'text-slate-700 min-w-[10rem]',
    render: (r) => <NowName value={r.clientGroup} now={r.clientGroupNow} className="text-slate-600" />,
  },
  {
    key: 'entityName',
    label: 'Entity Name',
    sortKey: 'entityName',
    className: 'text-slate-700 min-w-[14rem]',
    render: (r) => (r.entityName
      ? <NowName value={r.entityName} now={r.entityNameNow} />
      : <NowName value={r.engagementName} now={r.engagementNameNow ? shortEngagementName(r.engagementNameNow) : null} />),
  },
  { key: 'yearEnd', label: 'FYE', sortKey: 'yearEnd', render: (r) => <DateCell value={r.yearEnd} /> },
  // One company may hold several engagements for one FYE (one per service,
  // numbered when the service allows several) — without this column those
  // rows read identically.
  { key: 'service', label: 'Service', sortKey: 'serviceProvided', render: (r) => <ServiceCell r={r} /> },
  // Same one-line-with-tooltip treatment as the column beside it, so the two
  // lead columns do not read as two different kinds of thing.
  { key: 'partner', label: 'Partner', sortKey: 'partner', className: 'min-w-[8rem] max-w-[11rem]', render: (r) => <LeadsCell r={r} roles={['partner']} showMarks={false} /> },
  // Director and Manager share one column: both may hold several people, and
  // two more columns of their own would push this table wider still. No sort —
  // a cell holding several names of two roles has no one value to sort on.
  { key: 'directorManager', label: 'Director / Manager', className: 'min-w-[12rem] max-w-[16rem]', render: (r) => <LeadsCell r={r} /> },
  // 在審時點得開「在等誰簽核」（共用同一張小卡）。
  { key: 'stage', label: 'Current Stage', render: (r) => (r.currentStage ? <EngagementStageBadge stage={r.currentStage} engagementId={r.id} /> : <span className="text-slate-400">—</span>) },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    render: (r) => (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
        {r.status}
      </span>
    ),
  },
  ...(showAudit ? [
    { key: 'signOffDate', label: 'Sign Off', sortKey: 'signOffDate', render: (r) => <span className="whitespace-nowrap text-slate-600">{fmtDate(r.signOffDate)}</span> },
    { key: 'archivalDeadline', label: 'Archival Deadline', sortKey: 'archivalDeadline', render: (r) => <span className="whitespace-nowrap text-slate-600">{fmtDate(r.archivalDeadline)}</span> },
  ] : []),
];

export default engagementListColumns;
